import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { getUserSettingsAsync } from '../../../../store/slices/security/getUserSettingsSlice';
import {
  hasSeen2faSetupPrompt,
  mark2faSetupPromptSeen,
  navigateTo2faSetup,
} from './twoFactorPromptStorage';
import { TWO_FACTOR_SETUP_BANNER_HEIGHT } from './TwoFactorSetupBanner';

export function useTwoFactorSetupPrompt() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const wasOnSettingsRef = useRef(false);

  const { value: authValue } = useAppSelector((state) => state.auth);
  const userSettings = useAppSelector((state) => state.userSettings);

  const userId = authValue?.data?.user?.id;
  const hasMfaConfigured = Boolean(userSettings.value?.data?.mfaType);

  const [hasSeenPrompt, setHasSeenPrompt] = useState(() =>
    hasSeen2faSetupPrompt(userId),
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getUserSettingsAsync());
  }, [dispatch]);

  useEffect(() => {
    setHasSeenPrompt(hasSeen2faSetupPrompt(userId));
  }, [userId]);

  useEffect(() => {
    const isOnSettings = location.pathname.startsWith('/app/settings');

    if (wasOnSettingsRef.current && !isOnSettings) {
      dispatch(getUserSettingsAsync());
    }

    wasOnSettingsRef.current = isOnSettings;
  }, [dispatch, location.pathname]);

  useEffect(() => {
    if (!userId || hasMfaConfigured || hasSeenPrompt) {
      setIsModalOpen(false);
      return;
    }

    if (userSettings.status === 'success') {
      setIsModalOpen(true);
    }
  }, [userId, hasMfaConfigured, hasSeenPrompt, userSettings.status]);

  const dismissPrompt = useCallback(() => {
    mark2faSetupPromptSeen(userId);
    setHasSeenPrompt(true);
    setIsModalOpen(false);
  }, [userId]);

  const handleSetupNow = useCallback(() => {
    mark2faSetupPromptSeen(userId);
    setHasSeenPrompt(true);
    setIsModalOpen(false);
    navigateTo2faSetup(navigate, !hasMfaConfigured);
  }, [navigate, userId, hasMfaConfigured]);

  const settingsReady = Boolean(userSettings.value);

  const showBanner = useMemo(
    () =>
      Boolean(userId) &&
      !hasMfaConfigured &&
      hasSeenPrompt &&
      settingsReady,
    [userId, hasMfaConfigured, hasSeenPrompt, settingsReady],
  );

  const layoutOffset = showBanner ? TWO_FACTOR_SETUP_BANNER_HEIGHT : 0;

  return {
    isModalOpen,
    showBanner,
    layoutOffset,
    dismissPrompt,
    handleSetupNow,
  };
}
